import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import ReactSelect, { type StylesConfig } from 'react-select'
import type { User } from '../types/user'

interface OptionType {
  value: string
  label: string
}

interface UserSelectProps {
  label?: string
  selectedUser: User | null
  users: User[]
  onUserChange: (user: User | null) => void
  placeholder?: string
  isDisabled?: boolean
}

const UserSelect: React.FC<UserSelectProps> = ({
  label = 'Select User',
  selectedUser,
  users,
  onUserChange,
  placeholder = 'Select a user',
  isDisabled = false,
}) => {
  const options = users.map((user) => ({
    value: user.id.toString(),
    label: `${user.first_name} ${user.last_name}`.trim(),
  }))

  const selectedOption = selectedUser
    ? {
        value: selectedUser.id.toString(),
        label: `${selectedUser.first_name} ${selectedUser.last_name}`.trim(),
      }
    : undefined

  const handleChange = (option: { value: string; label: string } | null) => {
    if (option) {
      const user = users.find((u) => u.id.toString() === option.value)
      onUserChange(user || null)
    } else {
      onUserChange(null)
    }
  }

  // Simple custom styles for react-select to match form styling
  const customSelectStyles: StylesConfig<OptionType, false> = {
    control: (provided) => ({
      ...provided,
      minHeight: '48px',
      backgroundColor: '#F9FAFB',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      fontSize: '16px',
      fontFamily: 'Inter, sans-serif',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  }

  return (
    <div className={css(styles.container)}>
      <label className={css(styles.label)}>{label}</label>
      <ReactSelect
        placeholder={placeholder}
        options={options}
        value={selectedOption}
        onChange={handleChange}
        isDisabled={isDisabled}
        isClearable={false}
        isSearchable={true}
        styles={customSelectStyles}
        menuPlacement="auto"
        menuPosition="fixed"
      />
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
  },
  label: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '150%',
    color: '#111928',
  },
})

export default UserSelect
